import { useState, useEffect } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { getTrainingBudget } from '@/lib/services/budgets.service'
import type { TrainingBudget } from '@/types/budgets'
import { TrainingBudgetCard } from '@/components/budgets/TrainingBudgetCard'
import { TrainingHistoricalTable } from '@/components/budgets/TrainingHistoricalTable'

/**
 * Training Budget Page Route
 *
 * Director-only page for managing company-wide training budget.
 * Displays training budget and actual costs with variance visualization.
 *
 * Features:
 * - Year selector (2020-2099)
 * - Budget and actual cost display with currency formatting
 * - Automatic variance calculation
 * - Utilization percentage with color-coded progress bar
 * - Last updated metadata
 *
 * Access Control: Director role only
 */
export const Route = createFileRoute('/_auth/training/')({
  beforeLoad: () => {
    // Check if user is authenticated (basic check)
    // Full role check happens in component since we need React context
    const storedUserProfile = localStorage.getItem('user-profile')

    if (!storedUserProfile) {
      throw redirect({
        to: '/login',
      })
    }

    // Parse user to check role
    try {
      const user = JSON.parse(storedUserProfile)
      if (user.role !== 'diretor') {
        throw redirect({
          to: '/',
        })
      }
    } catch (error) {
      throw redirect({
        to: '/login',
      })
    }
  },
  component: TrainingPage,
})

function TrainingPage() {
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [trainingBudget, setTrainingBudget] = useState<TrainingBudget | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Double-check director role in component (defense in depth)
  useEffect(() => {
    if (user && user.role !== 'diretor') {
      window.location.href = '/'
    }
  }, [user])

  // Fetch training budget when year changes
  useEffect(() => {
    const fetchTrainingBudget = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const data = await getTrainingBudget(selectedYear)
        setTrainingBudget(data)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load training budget'
        setError(errorMessage)

        // If not found (404), show empty state instead of error
        if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          setTrainingBudget({
            year: selectedYear,
            budget: 0,
            actualCost: 0,
            variance: 0,
            utilizationPercent: 0,
            metadata: {
              lastUpdated: null,
              updatedBy: null,
            },
          })
          setError(null) // Clear error for empty state
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchTrainingBudget()
  }, [selectedYear])

  // Generate year options (2020-2099)
  const yearOptions = Array.from({ length: 80 }, (_, i) => 2020 + i)

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Training Budget</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage company-wide training budget and track actual expenditures
        </p>
      </div>

      {/* Year Selector */}
      <div className="mb-6">
        <label htmlFor="year-selector" className="block text-sm font-medium text-gray-700">
          Budget Year
        </label>
        <select
          id="year-selector"
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Training Budget Card */}
      {isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-gray-200"></div>
            <div className="h-8 w-1/2 rounded bg-gray-200"></div>
            <div className="h-4 w-1/4 rounded bg-gray-200"></div>
            <div className="h-8 w-1/2 rounded bg-gray-200"></div>
            <div className="h-4 w-full rounded bg-gray-200"></div>
          </div>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 shadow-sm">
          <h3 className="text-sm font-medium text-red-800">Error Loading Training Budget</h3>
          <p className="mt-2 text-sm text-red-700">{error}</p>
        </div>
      ) : trainingBudget ? (
        <>
          <TrainingBudgetCard
            budget={trainingBudget}
            year={selectedYear}
            onSave={(updatedBudget) => {
              // Update local state with the newly saved budget
              setTrainingBudget(updatedBudget)
            }}
          />

          {/* Historical Data Table */}
          <div className="mt-8">
            <TrainingHistoricalTable currentYear={selectedYear} />
          </div>
        </>
      ) : null}
    </div>
  )
}
